import { useState, useRef, useMemo, useEffect } from "react";
import { Button } from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { usePermissions } from "../../hooks/usePermissions";
import Table, { Column, useTable } from "../../Monolith-UI/Table/Table.js";
import ForensicSoftwareAPI from "../../api/software/index.js";
import { getDateFormat } from "../../utils/date-format";
import { useQueryFilter } from "../../Monolith-UI/QueryFilter/QueryFilter.js";
import ForensicSoftwareFilterDimensions from "../../components/ForensicSoftware/ForensicSoftwareFilterDimensions.js";
import { Helmet } from "react-helmet-async";
import ComboButton from "../../Monolith-UI/ComboButton/ComboButton.js";

import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import ZoomInMapOutlinedIcon from "@mui/icons-material/ZoomInMapOutlined";
import ReorderOutlinedIcon from "@mui/icons-material/ReorderOutlined";

import Loader from "../../components/Loader.js";
import styled, { useTheme } from "styled-components";
import synchronizeColumnState from "../../utils/synchronize-column-state.js";
import { useDebouncedCallback, useDebounce } from "use-debounce";
import { ItemTotalBadge } from "@/Features";
import { SaveIcon } from "lucide-react";
import AccessDeniedMessage from "../../components/AccessDeniedMessage.js";
import { CreateSoftwareModal } from "@/components/Modals";
import { Input, Typography } from "@monolith-forensics/monolith-ui";
import { ColumnProps, TableColumn } from "@/Monolith-UI/Table/types/Table.js";
import { Condition } from "../Cases/types/Cases.js";
import { SoftwareFlyout } from "@/components/Flyouts";
import { ForensicSoftwareColumnDefs } from "@/components/ColumnDefinitions";
import { ForensicSoftware } from "@/types";

interface QueryFilterRef {
  clear: () => void;
}

interface SoftwareQuery {
  query: {
    conditions?: Condition[];
    page?: number;
    search?: string | null;
    order?: {
      field: string;
      sort: string;
    } | null;
  };
}

const columnDefs = ForensicSoftwareColumnDefs;

const SoftwareSectionContent = styled(
  ({
    className,
    stateStoreKey = ["software", "view"],
    defaultQueryKey = ["software", "list"],
  }) => {
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();
    const theme: any = useTheme();
    const queryFilter = useRef<QueryFilterRef | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [pageSize, setPageSize] = useState(100);
    const [searchText, setSearchText] = useState(
      localStorage.getItem(`${stateStoreKey}:searchText`)
    );
    const [debouncedSearchText] = useDebounce(searchText, 500);

    const [columnState, setColumnState] = useState(() => {
      return synchronizeColumnState(
        columnDefs,
        JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
      );
    });

    const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
    const [flyoutData, setFlyoutData] = useState<ForensicSoftware | null>(null);

    const table = useTable();

    const currentSort = useMemo(() => {
      let [sort] = columnState
        ?.filter((c) => c.sorting?.active)
        .map((c) => ({ field: c.dataField, sort: c.sorting?.direction }));

      return sort;
    }, [columnState]);

    const [query, setQuery] = useState<SoftwareQuery | null>(null);

    const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
      useInfiniteQuery({
        queryKey: [
          defaultQueryKey,
          {
            query: {
              ...query?.query,
              order: query?.query?.order || currentSort,
              pageSize,
            },
          },
        ],
        queryFn: ({ pageParam }) =>
          ForensicSoftwareAPI.query({
            query: {
              ...query?.query,
              order: query?.query?.order || currentSort,
              pageSize,
              page: pageParam,
            },
          }),
        getNextPageParam: (lastPage, pages) => {
          return lastPage.nextPage;
        },
        getPreviousPageParam: (firstPage, pages) => {
          if (firstPage.page - 1 === 0) return null;
          return firstPage.page - 1;
        },
        initialPageParam: 1,
        enabled: !!query,
        placeholderData: (data) => data,
      });

    const records = data?.pages?.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, []);

    const totalRecords = data?.pages?.[0]?.total || 0;

    const debouncedFetchNextPage = useDebouncedCallback(() => {
      fetchNextPage();
    }, 50);

    const handleSort = (field: string) => {
      const savedColumn =
        columnState?.find((svc) => field === svc.dataField) || {};
      const order = !!savedColumn ? savedColumn?.sorting?.direction : null;

      let newOrder = null;

      // if sorted on a different column, reset the sort
      if (query?.query?.order?.field !== field) {
        newOrder = {
          field,
          sort: "asc",
        };
      }

      // otherwise rotate the sort options on the current column
      else {
        switch (order) {
          case "asc":
            newOrder = {
              field,
              sort: "desc",
            };
            break;
          case "desc":
            newOrder = null;
            break;
          default:
            newOrder = {
              field,
              sort: "asc",
            };
        }
      }

      let newCols = columnState?.map((c) => {
        if (c.dataField === field) {
          return {
            ...c,
            sorting: {
              active: newOrder ? true : false,
              direction: newOrder?.sort,
            },
          };
        }
        delete c.sorting;
        return c;
      });

      let newQuery = {
        query: {
          ...query?.query,
          order: newOrder,
        },
      };

      setColumnState(newCols);

      setQuery(newQuery);
    };

    const handleFilter = (newFilter: Condition) => {
      setQuery((q) => {
        return { query: { ...q?.query, ...newFilter, page: 1 } };
      });
    };

    const handleClearFilters = () => {
      queryFilter.current && queryFilter.current.clear();
    };

    const handleExportTable = () => {
      // show snackbar
      enqueueSnackbar("Exporting table...", {
        variant: "info",
      });

      ForensicSoftwareAPI.exportSoftwareList({
        query: {
          ...query?.query,
          order: query?.query?.order || currentSort,
          pageSize,
        },
        type: "xlsx",
        columns: columnState
          .filter((c) => c.visible !== false)
          .sort((a, b) => a.order - b.order)
          .map((c) => {
            return { dataField: c.dataField, header: c.caption, ...c };
          }),
        date_format: getDateFormat({ isMoment: true, includeTime: true }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).then((res) => {
        const { signedUrl, filename } = res;
        const el = document.createElement("a");
        el.href = signedUrl.replace(
          "http://localhost:3000",
          "http://localhost:3001"
        );

        el.download = filename;
        el.click();
        // remove snackbar
      });
    };

    const handleColumnReorder = (
      newOrder: { column: string; order: number }[]
    ) => {
      setColumnState((cs) => {
        return newOrder.map((o) => {
          return {
            ...cs.find((c) => c.dataField === o.column),
            order: o.order,
          };
        });
      });
    };

    const handleColumnVisibility = (
      column: ColumnProps<ForensicSoftware>,
      visible: boolean
    ) => {
      setColumnState((cs) => {
        return cs.map((c) => {
          if (c.dataField === column.dataField) {
            return {
              ...c,
              visible,
            };
          }
          return c;
        });
      });
    };

    const handleActionButtonClick = (rowData: ForensicSoftware) => {
      setIsFlyoutVisible(true);
      setFlyoutData(rowData);
    };

    const handleGetNextItem = () => {
      const currentId = flyoutData?.license_id;
      const current = records?.findIndex(
        (i: ForensicSoftware) => i.license_id === currentId
      );
      const nextItem = records[current + 1] || records[0];

      setFlyoutData(nextItem);
    };
    const handleGetPrevItem = () => {
      const currentId = flyoutData?.license_id;
      const current = records?.findIndex(
        (i: ForensicSoftware) => i.license_id === currentId
      );
      const prevItem = records[current - 1] || records[records.length - 1];

      setFlyoutData(prevItem);
    };
    const handleRefetchQuery = () => {
      refetch();
    };
    const onCreate = () => handleRefetchQuery();
    const onEdit = () => handleRefetchQuery();
    const onDelete = () => handleRefetchQuery();
    // Detect scroll to bottom
    const handleScroll = (e: React.MouseEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const pageLength = data?.pages?.length ? data?.pages?.length : 0;
      if (scrollHeight - scrollTop <= clientHeight + 100 * pageLength) {
        if (hasNextPage && !isFetchingNextPage) {
          debouncedFetchNextPage();
        }
      }
    };

    useEffect(() => {
      const localStorageStateValue = localStorage.getItem(stateStoreKey);
      let oldState = localStorageStateValue
        ? JSON.parse(localStorageStateValue)
        : {};
      localStorage.setItem(
        stateStoreKey,
        JSON.stringify({
          ...oldState,
          cols: columnState,
        })
      );
    }, [columnState]);

    useEffect(() => {
      setQuery((q) => ({
        query: {
          ...q?.query,
          search: debouncedSearchText ? debouncedSearchText : null,
          page: 1,
        },
      }));
    }, [debouncedSearchText]);

    const { queryButton, conditions } = useQueryFilter({
      dimensions: ForensicSoftwareFilterDimensions,
      onQuerySet: handleFilter,
      queryFilter: queryFilter,
      stateStoring: {
        enabled: true,
        type: "localStorage",
        storageKey: stateStoreKey,
      },
    });

    return (
      <div className={className}>
        <Helmet title="Forensic Software" />
        <Typography
          variant="h3"
          gutterBottom
          display="inline"
          style={{ margin: 0 }}
        >
          Forensic Software
        </Typography>
        <div
          style={{
            display: "flex",
            flex: "initial",
            flexDirection: "row",
            alignContent: "center",
            alignItems: "center",
            marginBottom: 10,
            marginTop: 10,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!hasPermission(MONOLITH_PERMISSIONS.SOFTWARE_CREATE)}
            onClick={(e) => {
              setShowCreateModal(true);
            }}
            style={{
              minWidth: "fit-content",
              fontSize: 11,
              padding: "3px 6px",
            }}
          >
            + New Software
          </Button>
          <div style={{ marginLeft: 10 }}>{queryButton}</div>
          <ItemTotalBadge total={totalRecords || 0} Icon={SaveIcon} />
          <div
            style={{
              marginLeft: "auto",
              display: "flex",
              alignContent: "center",
              alignItems: "center",
              minWidth: "fit-content",
            }}
          >
            <ComboButton
              type="multi-select"
              data={columnState.filter((c) => c.showInColumnChooser !== false)}
              displayField="caption"
              idField={"dataField"}
              selectedItems={columnState.filter((c) => c.visible !== false)}
              variant="outlined"
              closeOnSelect={false}
              showSearch={true}
              dropDownTitle={() => {
                return (
                  <div
                    style={{
                      margin: "5px 0px",
                      padding: 3,
                      color: theme.palette.text.secondary,
                      display: "flex",
                      alignItems: "center",
                      minWidth: 200,
                    }}
                  >
                    Select Columns
                  </div>
                );
              }}
              onItemDeSelect={(item) => {
                handleColumnVisibility(item, false);
              }}
              onItemSelect={(item) => {
                handleColumnVisibility(item, true);
              }}
              textColor={theme.palette.text.secondary}
              title={"Select Columns"}
            >
              <ViewColumnOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={"Export Table"}
              onClick={handleExportTable}
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={"Clear Filters"}
              onClick={handleClearFilters}
            >
              <FilterListOffIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant="outlined"
              textColor={theme.palette.text.secondary}
              title={table.isCompact ? "Zoom In" : "Zoom Out"}
              onClick={() => table.toggleCompact()}
            >
              {table.isCompact && (
                <ZoomOutMapOutlinedIcon style={{ fontSize: 18 }} />
              )}
              {!table.isCompact && (
                <ZoomInMapOutlinedIcon style={{ fontSize: 18 }} />
              )}
            </ComboButton>
            <ComboButton
              data={undefined}
              selectedItems={undefined}
              type="button"
              variant={"outlined"}
              textColor={
                table.isStriped
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary
              }
              title={table.isStriped ? "Hide Stripes" : "Show Stripes"}
              onClick={() => table.toggleStripes()}
            >
              <ReorderOutlinedIcon style={{ fontSize: 18 }} />
            </ComboButton>
            <Input
              placeholder="Search Software"
              variant="outlined"
              height={30}
              defaultValue={searchText}
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const currentValue = e.currentTarget.value.trim();
                setSearchText(currentValue);

                localStorage.setItem(
                  `${stateStoreKey}:searchText`,
                  currentValue
                );
              }}
              style={{
                marginLeft: "10px",
                border: searchText
                  ? `1px solid ${theme.palette.primary.main}`
                  : "",
              }}
            />
          </div>
        </div>
        {data && (
          <>
            {<div>{conditions}</div>}
            <Table
              data={records}
              totalRecords={totalRecords}
              keyValue="license_id"
              tableInstance={table}
              columnProps={{ minWidth: 150, width: 150 }}
              onHeaderClick={(col: TableColumn<ForensicSoftware>) =>
                col?.sorting?.enabled === false
                  ? null
                  : handleSort(col.dataField)
              }
              onScroll={handleScroll}
              onColumnReorder={handleColumnReorder}
              showActionColumn={true}
              onActionButtonClick={handleActionButtonClick}
              focusedRow={flyoutData}
            >
              {columnState.map((col) => {
                return <Column key={col.dataField} {...col} />;
              })}
            </Table>
          </>
        )}
        {!data && <Loader message={"Retrieving Items..."} />}
        {isFlyoutVisible && flyoutData && (
          <SoftwareFlyout
            open={isFlyoutVisible}
            onClose={() => {
              setIsFlyoutVisible(false);
              setFlyoutData(null);
            }}
            defaultInfo={flyoutData}
            onEdit={onEdit}
            onDelete={() => {
              setIsFlyoutVisible(false);
              setFlyoutData(null);
              onDelete();
            }}
            onNext={handleGetNextItem}
            onPrevious={handleGetPrevItem}
          />
        )}
        <CreateSoftwareModal
          onSubmit={onCreate}
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCancel={() => setShowCreateModal(false)}
        />
      </div>
    );
  }
)`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const SoftwareSection = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();

  if (!hasPermission(MONOLITH_PERMISSIONS.SOFTWARE_READ_ALL)) {
    return <AccessDeniedMessage />;
  }

  return <SoftwareSectionContent />;
};

export default SoftwareSection;
